import {
  useBankAddressStepsFlow,
  type BankAddressStepResponse,
} from '@/entities/bank-address';
import {
  useCreateBankAddressForm,
  CreateBankAddressForm,
  useCreateBankAddressFormLogic,
} from '@/features/create-bank-address';
import {
  CreateCardForm,
  useCreateCardForm,
  useCreateCardFormData,
  useCreateCardFormLogic,
  type CreateCardFormValues,
} from '@/features/create-card';
import {
  useCreateCryptoAddressForm,
  useCreateCryptoAddressFormData,
  useCreateCryptoAddressFormLogic,
  CreateCryptoAddressForm,
  type CreateCryptoAddressFormValues,
} from '@/features/create-crypto-address';
import {
  ApiErrorText,
  Button,
  Loader,
  ModalComponentWrapper,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreateSavedAddressModalTab } from '../model';

type Props = {
  onBack: () => void;
  onCreateSuccess: () => void;
  showOnlyTab?: CreateSavedAddressModalTab;
  initialCryptoFormValues?: Partial<CreateCryptoAddressFormValues>;
  initialCardFormValues?: Partial<CreateCardFormValues>;
  initialBankFormFields?: BankAddressStepResponse;
};

export function CreateSavedAddressModal({
  onBack,
  onCreateSuccess,
  showOnlyTab,
  initialCryptoFormValues,
  initialCardFormValues,
  initialBankFormFields,
}: Props) {
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState<CreateSavedAddressModalTab>(
    showOnlyTab || 'crypto'
  );

  // Crypto address form
  const { form: cryptoForm, selectedAssetCode } = useCreateCryptoAddressForm({
    initialValues: initialCryptoFormValues,
  });
  const { options, isLoading: isCryptoLoading } =
    useCreateCryptoAddressFormData({
      selectedAssetCode,
    });
  const {
    validateCryptoAddress,
    validateCryptoAddressDebounced,
    createAddress,
    isPending: isCryptoFormPending,
  } = useCreateCryptoAddressFormLogic({
    form: cryptoForm,
    networkOptions: options.networks,
    onSuccess: onCreateSuccess,
  });

  // Bank address form
  const {
    fields,
    isLoading: isBankLoading,
    isPending: isBankFormPending,
    error,
    submitStep,
    isFirstStep,
    isLastStep,
  } = useBankAddressStepsFlow({ initialFields: initialBankFormFields });
  const { form: bankForm } = useCreateBankAddressForm({ fields, isLastStep });
  const { submitBankAddress, isPending: isCreateBankAddressPending } =
    useCreateBankAddressFormLogic({
      form: bankForm,
      fields,
      isLastStep,
      onSuccessCreateBankAddress: onCreateSuccess,
      submitStep,
    });

  // Card form
  const { form: cardForm } = useCreateCardForm({
    initialValues: initialCardFormValues,
  });
  const {
    currencies,
    cardIconConfig,
    isLoading: isCardLoading,
  } = useCreateCardFormData();
  const { createCard, isPending: isCreateCardPending } = useCreateCardFormLogic(
    {
      form: cardForm,
      onSuccess: onCreateSuccess,
    }
  );

  // Loading states
  const isLoading = isCryptoLoading || isBankLoading || isCardLoading;
  const isPending =
    isCryptoFormPending ||
    isBankFormPending ||
    isCreateBankAddressPending ||
    isCreateCardPending;

  const title = t(
    showOnlyTab
      ? 'createSavedAddress.titleSavingAddress'
      : 'createSavedAddress.title'
  );

  if (isLoading) {
    return (
      <ModalComponentWrapper
        title={title}
        onBack={onBack}
        headerClassName="text-secondary-bg bg-secondary-bg/50">
        <Loader />
      </ModalComponentWrapper>
    );
  }

  if (error) {
    return (
      <ModalComponentWrapper
        title={title}
        onBack={onBack}
        headerClassName="text-secondary-bg bg-secondary-bg/50">
        <ApiErrorText error={error} />
      </ModalComponentWrapper>
    );
  }

  const handleSubmit = async () => {
    const submitHandlers = {
      crypto: createAddress,
      bank: submitBankAddress,
      card: createCard,
    };

    await submitHandlers[selectedTab]?.();
  };

  const handleBack = () => {
    if (selectedTab === 'crypto' || selectedTab === 'card') {
      return onBack();
    }

    // Bank address logic
    if (isFirstStep) {
      onBack();
    }
  };

  const getButtonTextKey = () => {
    if (showOnlyTab) {
      return 'common.saveAndFinish';
    }

    if (selectedTab === 'crypto') {
      return 'savedAddresses.cryptoAddress.creation.buttonText';
    }

    if (selectedTab === 'card') {
      return 'savedAddresses.card.creation.buttonText';
    }

    if (selectedTab === 'bank') {
      return isLastStep ? 'common.submit' : 'common.continue';
    }

    return 'common.continue';
  };

  return (
    <ModalComponentWrapper
      title={title}
      onBack={handleBack}
      footer={
        !isLoading && (
          <Button
            size="lg"
            type="submit"
            loading={isPending}
            onClick={handleSubmit}
            className="mt-[1.6rem]">
            {t(getButtonTextKey())}
          </Button>
        )
      }>
      <Tabs
        value={selectedTab}
        onValueChange={value =>
          setSelectedTab(value as CreateSavedAddressModalTab)
        }
        className="w-full">
        <TabsList className="w-full mb-[2.4rem]" hidden={Boolean(showOnlyTab)}>
          <TabsTrigger value="crypto">
            {t('createSavedAddress.tabs.crypto')}
          </TabsTrigger>
          <TabsTrigger value="bank">
            {t('createSavedAddress.tabs.bank')}
          </TabsTrigger>
          <TabsTrigger value="card">
            {t('createSavedAddress.tabs.card')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="crypto">
          <CreateCryptoAddressForm
            form={cryptoForm}
            assetOptions={options.assets}
            networkOptions={options.networks}
            validateCryptoAddress={validateCryptoAddress}
            validateCryptoAddressDebounced={validateCryptoAddressDebounced}
            initialValues={initialCryptoFormValues}
          />
        </TabsContent>

        <TabsContent value="bank">
          {fields && (
            <CreateBankAddressForm
              form={bankForm}
              fields={fields}
              isLastStep={isLastStep}
            />
          )}
        </TabsContent>

        <TabsContent value="card">
          <CreateCardForm
            form={cardForm}
            currencies={currencies}
            cardIconConfig={cardIconConfig}
            initialValues={initialCardFormValues}
          />
        </TabsContent>
      </Tabs>
    </ModalComponentWrapper>
  );
}
